import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../Component/axios_Interceptor/api';
import url, { SERVER_ADDRESS } from '../../Component/server';

export type ChatRestriction = { blocked: boolean; muted: boolean };
export function publishLiveBan(nickname: string, blocked: boolean) {
  window.dispatchEvent(new CustomEvent('fanbird:live-ban-updated', { detail: { nickname, blocked } }));
}
export default function useChatRestrictions(liveSeq: number | undefined, enabled: boolean) {
  const [state, setState] = useState<{ banned: Set<string>; muted: Set<string>; ready: boolean } | null>(null);
  const fromSocket = useRef(false);
  const updates = useRef(new Map<string, Partial<ChatRestriction>>());
  const apply = useCallback((banned: string[], muted: string[]) => {
    const next = { banned: new Set(banned), muted: new Set(muted), ready: true };
    // A list started before a confirmed action must not undo that action.
    for (const [name, local] of updates.current) {
      for (const [key, list] of [['blocked', next.banned], ['muted', next.muted]] as const) {
        if (local[key] === undefined) continue;
        if (list.has(name) === local[key]) delete local[key];
        else if (local[key]) list.add(name); else list.delete(name);
      }
      if (!Object.keys(local).length) updates.current.delete(name);
    }
    setState(next);
  }, []);
  const acceptSocketState = useCallback((data: any) => {
    if (!enabled || data?.type !== 'moderation-state' || !Array.isArray(data.banned) || !Array.isArray(data.muted)
      || [...data.banned, ...data.muted].some(name => typeof name !== 'string')) return false;
    fromSocket.current = true; apply(data.banned, data.muted); return true;
  }, [enabled, apply]);
  useEffect(() => {
    setState(null); fromSocket.current = false; updates.current.clear();
    if (!enabled || !liveSeq) return;
    let active = true, busy = false;
    const load = async () => {
      if (!active || busy || fromSocket.current) return;
      busy = true;
      try {
        const list = async (endpoint: string) => {
          const names: string[] = [];
          for (let page = 1; active; page++) {
            const { data } = await api.get(endpoint, { params: { current_page: page, per_page: 100 }, timeout: 10000 });
            if (!Array.isArray(data?.userList) || !Number.isSafeInteger(data.total) || data.total < 0) throw new Error('Invalid restriction list');
            data.userList.forEach((row: { nickname?: string }) => { if (typeof row.nickname === 'string') names.push(row.nickname); });
            if (page * 100 >= data.total) return names;
            if (!data.userList.length) throw new Error('Incomplete restriction list');
          }
          return names;
        };
        const [banned, muted] = await Promise.all([list(url.Communication_url.ban_user_list), list(`${SERVER_ADDRESS}relation/chat-muted-user-list`)]);
        if (active && !fromSocket.current) apply(banned, muted);
      } catch { /* Do not guess an unrestricted state on lookup failure. */ }
      finally { busy = false; }
    };
    const changed = (event: Event) => {
      const data = (event as CustomEvent).detail;
      const key = event.type === 'fanbird:live-ban-updated' ? 'blocked' : 'muted';
      if (typeof data?.nickname !== 'string' || typeof data[key] !== 'boolean') return;
      updates.current.set(data.nickname, { ...updates.current.get(data.nickname), [key]: data[key] });
      setState(current => {
        const next = { banned: new Set(current?.banned), muted: new Set(current?.muted), ready: current?.ready ?? false };
        const list = key === 'blocked' ? next.banned : next.muted;
        if (data[key]) list.add(data.nickname); else list.delete(data.nickname);
        return next;
      });
    };
    // Older realtime nodes fall back once on page entry, never on a nickname click.
    const timer = window.setTimeout(load, 500);
    window.addEventListener('focus', load);
    window.addEventListener('fanbird:chat-mute-updated', changed); window.addEventListener('fanbird:live-ban-updated', changed);
    return () => { active = false; clearTimeout(timer); window.removeEventListener('focus', load);
      window.removeEventListener('fanbird:chat-mute-updated', changed); window.removeEventListener('fanbird:live-ban-updated', changed); };
  }, [liveSeq, enabled, apply]);
  return { state, acceptSocketState };
}
